import React, { useState } from 'react';

import Tooltip from 'rc-tooltip';

import type { ShapeElementType } from 'Components/CanvasEditor/types';
import { FlexBox } from '@eltoro-ui/components';

interface ShapeContentProps {
  selectedElement: ShapeElementType;
  onElementChange: (props: Partial<ShapeElementType>, id: string, saveToHistory?: boolean) => void;
}

export default function ShapeContent({ selectedElement, onElementChange }: ShapeContentProps) {
  const [changeSeparately, setChangeSeparately] = useState(false);

  const renderRectChangeRadiusProperties = (cornerRadius: number | number[]) => {
    if (Array.isArray(cornerRadius)) {
      const [topLeft = 0, topRight = 0, bottomRight = 0, bottemLeft = 0] = cornerRadius;

      return (
        <>
          <div className="property-row">
            <div className="property-label">Top Left</div>
            <div className="property-value">
              <div className="range-input">
                <input
                  type="range"
                  min="0"
                  max={Math.floor(
                    Math.max(selectedElement.width ?? 0, selectedElement?.height ?? 0) / 2
                  )}
                  value={topLeft}
                  onChange={e =>
                    onElementChange(
                      {
                        cornerRadius: [
                          parseInt(e.target.value, 10),
                          topRight,
                          bottomRight,
                          bottemLeft,
                        ],
                      },
                      selectedElement.id,
                      true
                    )
                  }
                />
                <div className="range-value">{topLeft}px</div>
              </div>
            </div>
          </div>
          <div className="property-row">
            <div className="property-label">Top Right</div>
            <div className="property-value">
              <div className="range-input">
                <input
                  type="range"
                  min="0"
                  max={Math.floor(
                    Math.max(selectedElement.width ?? 0, selectedElement.height ?? 0) / 2
                  )}
                  value={topRight}
                  onChange={e =>
                    onElementChange(
                      {
                        cornerRadius: [
                          topLeft,
                          parseInt(e.target.value, 10),
                          bottomRight,
                          bottemLeft,
                        ],
                      },
                      selectedElement.id,
                      true
                    )
                  }
                />
                <div className="range-value">{topRight}px</div>
              </div>
            </div>
          </div>
          <div className="property-row">
            <div className="property-label">Bottom Right</div>
            <div className="property-value">
              <div className="range-input">
                <input
                  type="range"
                  min="0"
                  max={Math.floor(
                    Math.max(selectedElement.width ?? 0, selectedElement.height ?? 0) / 2
                  )}
                  value={bottomRight}
                  onChange={e =>
                    onElementChange(
                      {
                        cornerRadius: [topLeft, topRight, parseInt(e.target.value, 10), bottemLeft],
                      },
                      selectedElement.id,
                      true
                    )
                  }
                />
                <div className="range-value">{bottomRight}px</div>
              </div>
            </div>
          </div>
          <div className="property-row">
            <div className="property-label">Bottom Left</div>
            <div className="property-value">
              <div className="range-input">
                <input
                  type="range"
                  min="0"
                  max={Math.floor(
                    Math.max(selectedElement.width ?? 0, selectedElement.height ?? 0) / 2
                  )}
                  value={bottemLeft}
                  onChange={e =>
                    onElementChange(
                      {
                        cornerRadius: [
                          topLeft,
                          topRight,
                          bottomRight,
                          parseInt(e.target.value, 10),
                        ],
                      },
                      selectedElement.id,
                      true
                    )
                  }
                />
                <div className="range-value">{bottemLeft}px</div>
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <div className="property-row">
        <div className="property-label">Radius</div>
        <div className="property-value">
          <div className="range-input">
            <input
              type="range"
              min="0"
              max={Math.floor(
                Math.max(selectedElement.width ?? 0, selectedElement.height ?? 0) / 2
              )}
              value={cornerRadius}
              onChange={e =>
                onElementChange(
                  { cornerRadius: parseInt(e.target.value, 10) },
                  selectedElement.id,
                  true
                )
              }
            />
            <div className="range-value">{cornerRadius}px</div>
          </div>
        </div>
      </div>
    );
  };

  const overlay = () => {
    if (selectedElement?.type === 'rect') {
      return (
        <div className="property-group">
          <div className="group-title">Corner Radius</div>
          <FlexBox alignItems="center" gap="0.25rem" UNSAFE_style={{ marginBottom: '0.5rem' }}>
            <span className="">Change Separately</span>
            <label aria-label="change-separately" htmlFor="change-separately" className="switch">
              <input
                id="change-separately"
                type="checkbox"
                checked={changeSeparately}
                onChange={event => {
                  const { checked } = event.target;
                  onElementChange(
                    {
                      cornerRadius: checked
                        ? [
                            selectedElement.cornerRadius,
                            selectedElement.cornerRadius,
                            selectedElement.cornerRadius,
                            selectedElement.cornerRadius,
                          ]
                        : Math.max(...(selectedElement?.cornerRadius as number[])),
                    },
                    selectedElement.id
                  );
                  setChangeSeparately(checked);
                }}
              />
              <span className="slider" />
            </label>
          </FlexBox>
          {renderRectChangeRadiusProperties(selectedElement.cornerRadius ?? 0)}
        </div>
      );
    }
    return null;
  };

  if (selectedElement?.type !== 'rect') return null;

  return (
    <>
      <Tooltip
        placement="bottom"
        showArrow={false}
        classNames={{ root: 'property-tooltip' }}
        trigger="click"
        overlay={overlay()}
      >
        <span className="image-mask">Corner Radius</span>
      </Tooltip>
    </>
  );
}
